import {MouseEvent, useCallback, useState} from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {makeStyles} from '@mui/styles';
import Chip from '@mui/material/Chip';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import BugReportIcon from '@mui/icons-material/BugReport';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import {IProcessRequestProps} from '../../react_query/processingCenter/processingCenterQuery';
import ProcessingPopOver from './ProcessingPopOver';
import MusicPreviewBtn from '../../components/music/MusicPreviewBtn';
type Props = {
  val: IProcessRequestProps;
  handleProcess: (id: number, type: string) => void;
};
export const description: {[key: string]: string} = {};
description['rerecord'] = 'Re-record requested ';
description['reset'] = 'Reset performed ';
description['quality'] = 'Quality issue reported ';

export const truncateText = (text: string, maxLength: number) => {
  return text?.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};
function capitalizeFirstLetter(val: string) {
  return val[0] ? val[0].toUpperCase() + val.slice(1) : val;
}
const useStyles = makeStyles(() => ({
  completed: {
    background: '#4CAF50',
    color: 'white',
    width: '100',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#4CAF50',
    },
  },
  inprogress: {
    background: '#ea8029',
    color: 'white',
    width: '100',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#ea8029',
    },
  },
  pending: {
    background: '#b98000',
    color: 'white',
    width: '100',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#b98000',
    },
  },
  failed: {
    background: '#9f1212',
    color: 'white',
    width: '100',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#9f1212',
    },
  },
}));

const ProcessingTabContent = ({val, handleProcess}: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);
  const handleClick = useCallback(
    (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  let buttonIcon = <RestartAltIcon />;
  let name = '';
  let type = '';
  if (val.is_manual_upload === 0 && val.requestable_type === 'message') {
    buttonIcon = <RecordVoiceOverIcon />;
    name = 'Rerecord';
    type = 'rerecord';
  } else if (val.is_manual_upload === 1 && val.requestable_type === 'message') {
    buttonIcon = <BugReportIcon />;
    name = 'Quality';
    type = 'quality';
  } else if (val.requestable_type !== 'message') {
    buttonIcon = <RestartAltIcon />;
    name = 'Reset';
    type = 'reset';
  }
  let fileUrl = '';
  if (val.file_path) {
    const fileDet =
      typeof val.file_path === 'string'
        ? JSON.parse(val.file_path)
        : val.file_path;
    fileUrl = fileDet.file_url;
  }
  let processFileUrl = '';
  if (
    (val.status === 'completed' || val.status === 'closed') &&
    val.meta_data_file_path
  ) {
    const fileDet =
      typeof val.meta_data_file_path === 'string'
        ? JSON.parse(val.meta_data_file_path)
        : val.meta_data_file_path;
    processFileUrl = fileDet.file_url;
  }

  return (
    <Grid item xs={3}>
      <Card elevation={3} className='blue-variant17'>
        <CardContent className='p-4'>
          <Box>
            <Grid container>
              <Grid item xs={10}>
                <Tooltip title={val.file_name} describeChild>
                  <Typography className='text-white font-bold'>
                    {truncateText(val.file_name, 25)}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={2}>
                {fileUrl && val.file_deleted_at === null ? (
                  <Box className='flex items-center justify-end gap-2'>
                    <MusicPreviewBtn
                      id={`${val.id}${fileUrl}`}
                      url={fileUrl}
                      track_name={val.file_name}
                      track_artist={val.file_name}
                      toolTipLabel='Initial file'
                    />
                    <a
                      href={fileUrl}
                      download={val.file_name}
                      target='_blank'
                      className='flex'
                      aria-label={`Download ${val.file_name}`}
                      rel='noreferrer'>
                      <DownloadIcon />
                    </a>
                  </Box>
                ) : null}
              </Grid>
            </Grid>
            {val.file_deleted_at && (
              <Typography style={{fontStyle: 'italic'}}>
                Purged on {val.file_deleted_at}
              </Typography>
            )}
            <Tooltip title={val.company?.name} describeChild>
              <Typography
                variant='subtitle1'
                color='text.secondary'
                component='div'>
                {truncateText(val.company?.name, 30)}
              </Typography>
            </Tooltip>
            <Typography
              variant='subtitle2'
              className='flex items-center justify-between mt-2'
              color='text.secondary'
              component='div'>
              {dayjs(val.request_date).format('MM/DD/YYYY hh:ss A')}
              <Chip
                label={capitalizeFirstLetter(val.status)}
                size='small'
                className={
                  classes[
                    val.status as
                      | 'completed'
                      | 'inprogress'
                      | 'pending'
                      | 'failed'
                  ]
                }
              />

              {val.processing_center_notes &&
                val.processing_center_notes.length > 0 && (
                  <Typography
                    variant='subtitle2'
                    color='text.secondary'
                    component='div'
                    key={val.processing_center_notes[0]?.id}
                    sx={{
                      fontStyle: 'italic',
                      fontSize: '12px',
                    }}>
                    {val.processing_center_notes[0] &&
                    val.processing_center_notes[0]?.type !== undefined
                      ? description[val.processing_center_notes[0]?.type]
                      : ''}
                    {val.processing_center_notes[0]?.user?.name}
                    {dayjs(val.processing_center_notes[0]?.created_at).format(
                      'MM/DD/YYYY hh:mm:ss A',
                    )}
                    <HistoryIcon
                      onClick={
                        handleClick as unknown as React.MouseEventHandler<SVGSVGElement>
                      }
                    />
                    <ProcessingPopOver
                      open={open}
                      anchorEl={anchorEl}
                      handleClose={handleClose}
                      notes={val.processing_center_notes}
                    />
                  </Typography>
                )}
            </Typography>
          </Box>
          <Box className='flex items-center justify-between mt-3'>
            <Grid container>
              <Grid item xs={8}>
                {val.status === 'completed' ||
                (val.status === 'closed' && processFileUrl) ? (
                  <Box className='flex items-center justify-end gap-2'>
                    <MusicPreviewBtn
                      id={`${val.id}${processFileUrl}`}
                      url={processFileUrl}
                      track_name={val.file_name}
                      track_artist={val.file_name}
                      toolTipLabel='Final file'
                    />
                    <a
                      href={processFileUrl}
                      download={val.file_name}
                      target='_blank'
                      className='flex'
                      aria-label={`Download ${val.file_name}`}
                      rel='noreferrer'>
                      <DownloadIcon />
                    </a>
                  </Box>
                ) : null}
              </Grid>
              <Grid xs={4} className='flex justify-end'>
                {name && type && (
                  <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    onClick={() => handleProcess(val.id, type)}
                    startIcon={buttonIcon}>
                    <ListItemText primary={name} />
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ProcessingTabContent;
